import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Lock, LogIn, AlertCircle, CheckCircle } from 'lucide-react';
import api from "../services/api";

export default function LoginForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'กรุณากรอกชื่อผู้ใช้';
    }

    if (!formData.password) {
      newErrors.password = 'กรุณากรอกรหัสผ่าน';
    } else if (formData.password.length < 4) {
      newErrors.password = 'รหัสผ่านต้องมีอย่างน้อย 4 ตัวอักษร';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;
    setIsLoading(true);

    try {
      const res = await api.post("/login", {
        username: formData.username,
        password: formData.password,
      });
      const data = res.data;
      setIsLoading(false);

      if (data.success) {
        setLoginSuccess(true);

        localStorage.setItem("role", data.role || "user");
        if (data.token) localStorage.setItem("token", data.token);
        if (data.userId) localStorage.setItem("userId", data.userId);

        const redirect = searchParams.get("redirect") || "/";
        setTimeout(() => {
          setLoginSuccess(false);
          if (redirect.startsWith("/upload") && (data.role || "").toLowerCase() !== "student") {
            alert("บัญชีนี้ไม่มีสิทธิ์อัปโหลดเอกสาร");
            navigate("/");
            return;
          }
          navigate(redirect);
        }, 1200);
      } else {
        setErrors({ password: "ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง" });
      }
      } catch (err) {
        console.error("Login error:", err);
        console.log("Server data:", err?.response?.data);
        console.log("Server message:", err?.response?.data?.message);
        setIsLoading(false);
        setErrors({ password: err?.response?.data?.message || "เกิดข้อผิดพลาด กรุณาลองใหม่" });
      }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-brand-50 to-brand-100 ">
      <div className="bg-white/60 backdrop-blur p-6 rounded-3xl shadow-2xl w-full max-w-md flex flex-col items-center justify-center border border-brand-100">
        <div className="bg-white/80 backdrop-blur rounded-2xl shadow-xl p-8 border border-brand-100 w-full">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-brand-500 to-brand-600 rounded-full mb-4">
              <LogIn className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">เข้าสู่ระบบ</h2>
            <p className="text-gray-600">กรอกชื่อผู้ใช้และรหัสผ่านเพื่อเข้าสู่ระบบ</p>
          </div>

          {/* Success Message */}
          {loginSuccess && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center space-x-3 animate-in slide-in-from-top-2 duration-300">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-green-800 font-medium">เข้าสู่ระบบสำเร็จ!</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6 bg-brand-50/60 p-6 rounded-xl shadow-inner border border-brand-200">
            {/* Username Field */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                ชื่อผู้ใช้
              </label>
              <div className="relative">
                <input
                  id="username"
                  type="text"
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  className={`block w-full pl-3 pr-3 py-3 border rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors duration-200 ${
                    errors.username
                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                      : 'border-brand-200'
                  }`}
                  placeholder="กรอกชื่อผู้ใช้"
                />
                {errors.username && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  </div>
                )}
              </div>
              {errors.username && (
                <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                  <span>{errors.username}</span>
                </p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                รหัสผ่าน
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="w-5 h-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  className={`block w-full pl-10 pr-10 py-3 border rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors duration-200 ${
                    errors.password
                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                      : 'border-brand-200'
                  }`}
                  placeholder="กรอกรหัสผ่าน"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center hover:text-gray-700 transition-colors duration-200"
                >
                  {showPassword ? (
                    <Eye className="w-5 h-5 text-gray-400" />
                  ) : (
                    <EyeOff className="w-5 h-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                  <span>{errors.password}</span>
                </p>
              )}
            </div>

            {/* Forgot Password only */}
            <div className="flex items-center justify-center">
              <button
                type="button"
                className="text-sm text-brand-700 hover:text-brand-800 font-medium transition-colors duration-200"
                onClick={async () => {
                  const username = prompt('ระบุชื่อผู้ใช้เพื่อรีเซ็ตรหัสผ่าน');
                  if (!username) return;
                  try {
                   const res = await api.post("/auth/forgot-password", { username });
                    const data = res.data;
                    if (!data.success) {
                      return alert(data.message || "ส่งรหัสรีเซ็ตไม่สำเร็จ");
                    }
                    const code = prompt(`กรอกรหัส OTP ที่ได้รับ (เดโม: ${data.code})`);
                    if (!code) return;
                    const newPass = prompt('กรอกรหัสผ่านใหม่');
                    if (!newPass) return;
                    const res2 = await api.post("/auth/reset-password", {
                      username,
                      code,
                      new_password: newPass,
                    });
                    const data2 = res2.data;
                    if (!data2.success) {
                      return alert(data2.message || "รีเซ็ตรหัสผ่านไม่สำเร็จ");
                    }
                    alert('รีเซ็ตรหัสผ่านสำเร็จ ลองเข้าสู่ระบบใหม่');
                  } catch (e) {
                    console.error(e);
                    alert('เกิดข้อผิดพลาด');
                  }
                }}
              >
                ลืมรหัสผ่าน?
              </button>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-brand-600 to-brand-700 hover:from-brand-700 hover:to-brand-800 text-white font-semibold py-3 px-4 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>กำลังเข้าสู่ระบบ...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center space-x-2">
                  <LogIn className="w-5 h-5" />
                  <span>เข้าสู่ระบบ</span>
                </div>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600">
              ยังไม่มีบัญชีใช่ไหม?{' '}
              <button
                onClick={() => navigate("/signup")}
                className="text-brand-700 hover:text-brand-800 font-medium transition-colors duration-200">
                ลงทะเบียนที่นี่
              </button>

            </p>
          </div>
        </div>
      </div>
    </div>
  );
}